/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { validateEmailFormat, validatePasswordFormat } from '@touchplan/common';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { valid_url } from '../../app-data/constants/regex-patterns';
import { CUSTOM_FIELD_INVALID_LINK, CUSTOM_FIELD_UNALLOWED_LINK } from '@touchplan/strings';
import { DurationUtils } from '../../utils/duration-utils';

/**
 * Creates a validator function for the date range values of the 2 form controls with the names given.
 * Validate returns true if the start date control's value is after the end date control's value or returns the
 * error  { invalidDateRange: true }.
 * @param startDateControlName the name of the control containing the start date.
 * @param endDateControlName the name of the control containing the end date.
 */
export function dateRangeStringValidator(
	startDateControlName: string,
	endDateControlName: string
): ValidatorFn {
	return (ticketForm: AbstractControl): ValidationErrors | null => {
		const startDateControl = ticketForm.get(startDateControlName);
		const finishDateControl = ticketForm.get(endDateControlName);
		if (startDateControl && finishDateControl) {
			const startDate = startDateControl.value;
			const finishDate = finishDateControl.value;
			if (startDate > finishDate) {
				return { invalidDateRange: true };
			}
		}
		return null;
	};
}

export const durationHoursValidator: ValidatorFn = (
	liveDurationHours: AbstractControl
): ValidationErrors | null => {
	if (!liveDurationHours.value) {
		return { invalidDurationHours: true };
	}
	const { hours, minutes } = DurationUtils.getHoursMinutesSeconds(liveDurationHours.value);
	const totalHours = hours + minutes / 60;
	if (hours < 1 && minutes < 1) {
		return { invalidDurationHours: true };
	} else if (totalHours > 9600) {
		return { invalidMaxDurationHours: true };
	}

	let retValue: any = null;
	if (hours < 0) {
		retValue = { invalidHours: true };
	}
	if (minutes < 0) {
		if (retValue) {
			retValue.invalidMinutes = true;
		} else {
			retValue = { invalidMinutes: true };
		}
	}
	return retValue;
};

export function emailFormatValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!validateEmailFormat(control?.value)) {
			return { email: true };
		}

		return null;
	};
}

export function passwordFormatValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!validatePasswordFormat(control?.value)) {
			return { notMatchRequirements: true };
		}

		return null;
	};
}

/**
 * Validates that the password and confirmPassword fields match.
 * @param passwordFormControlName The name of the password form control. Defaults to 'password'.
 * @param confirmPasswordFormControlName The name of the confirmPassword form control. Defaults to 'confirmPassword'.
 * Note: This validator should be added to the parent FormGroup of the password and confirmPassword form controls.
 * This validator should use matcher = new ParentErrorStateMatcher() in the template.
 * [errorStateMatcher]="matcher"
 */
export function passwordMatchValidator(
	passwordFormControlName = 'password',
	confirmPasswordFormControlName = 'confirmPassword'
): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const password = control?.get(passwordFormControlName);
		const confirmPassword = control?.get(confirmPasswordFormControlName);
		if (password?.value !== confirmPassword?.value && !confirmPassword?.pristine) {
			return { passwordNotMatch: true };
		} else {
			return null;
		}
	};
}

/**
 * Custom validator for the url based on the regular expressing given.
 * @param allowedRegex the regular expression for valid allowed urls.
 */
export function validCustomLink(allowedRegex: RegExp): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const validUrl = valid_url.test(control.value);
		if (control.value && !validUrl) {
			return { invalidUrl: CUSTOM_FIELD_INVALID_LINK };
		} else if (control.value && !allowedRegex.test(control.value)) {
			return { invalidUrl: CUSTOM_FIELD_UNALLOWED_LINK };
		} else {
			return null; // everything valid
		}
	};
}
